import { useEffect, useMemo, useCallback } from 'react'
import useEffectOnce from '@hook/useEffectOnce'
import useTemplateFieldsListViewModel from './TemplateFieldsListViewModel'
import useCheckableItems from '@hook/useCheckableItems'
import { noop } from '@utils'
import cn from 'clsx'

import SortableList from '@components/shared/DraggableLists/SortableList'
import Checkbox from '$components/Forms/Checkbox'
import { MdInfoOutline } from 'react-icons/md'
import { FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa'
import '@components/shared/DraggableLists/DraggableLists.css'

const TemplateFieldsList = ({
    className,
    template,
    onDirtyChange = noop,
    onSelectedChange,
}) => {
    
    const {
        templateFields,
        availableFields,
    } = useTemplateFieldsListViewModel(template)
    
    const selectedFields = useCheckableItems(templateFields)
    const allSelectedFields = useCheckableItems(availableFields)
    
    const selectedTemplateFields = useMemo(() => {
        
        if (!availableFields?.length || !selectedFields?.checked?.length)
            return []
        
        return selectedFields.checked.map(id => {
            return availableFields.find(it => it?.id === id)
        })
        
    }, [availableFields, selectedFields.checked])
    
    const setSelectedFields = useCallback(op => {
        
        const items = typeof op === 'function'
            ? op(selectedTemplateFields)
            : op
        
        selectedFields.setChecked(items.map(it => it.id), true)
        
    }, [selectedTemplateFields])
    
    useEffect(() => {
        
        if (allSelectedFields.checked.length === selectedFields.checked.length) return
        
        selectedFields.updateChecked(prev => {
            
            prev = prev.filter(id => allSelectedFields.checked.includes(id))
            
            const newIds = allSelectedFields.checked
                .filter(id => !prev.includes(id))
            
            if (newIds.length)
                prev = [...prev, ...newIds]
            
            return prev
            
        })
        
    }, [allSelectedFields.checked])
    
    useEffect(() => {
        
        // Important: don't remove this check, or be cursed to a debugging nightmare
        if (!templateFields || !selectedTemplateFields)
            return
        
        onSelectedChange(selectedTemplateFields)
        
        // First check if the lengths are different, since this is faster
        if (templateFields.length !== selectedTemplateFields.length)
            return onDirtyChange(true)
        
        // Now also check if the order has changed, since a user can
        // reorder fields without adding/removing any
        // We can safely access both arrays by index,
        // since we now know they're the same length
        let isSameOrder = true
        
        for (let i = 0, m = templateFields.length; i < m; i++) {
            
            if (templateFields[i].id === selectedTemplateFields[i].id)
                continue
            
            isSameOrder = false
            break
            
        }
        
        if (!isSameOrder)
            return onDirtyChange(true)
        
        const ids = templateFields.map(it => it.id).sort()
        const selectedIds = selectedTemplateFields.map(it => it.id).sort()
        
        onDirtyChange(ids.toString() !== selectedIds.toString())
        
    }, [templateFields, selectedTemplateFields])
    
    useEffectOnce(() => {
        
        if (!templateFields?.length) return false
        if (!allSelectedFields?.setChecked) return false
        
        const ids = templateFields.map(it => it.id)
        
        if (!allSelectedFields.hasChecked(ids))
            allSelectedFields.setChecked(ids)
        
        selectedFields.setChecked(ids)
        
        return true
        
    }, [templateFields, allSelectedFields])
    
    return (
        
        <div className={cn('TemplateFieldsList grid grid-cols-12 gap-4', className)}>
            
            <div className="col-span-5 DraggableList">
                <div className="flex items-center content-center justify-center">
                    <div className="w-full h-px mb-2 bg-gray-500 shrink" />
                    <h4 className="mx-3 mb-2 text-base grow shrink-0">
                        Available Fields
                    </h4>
                    <div className="w-full h-px mb-2 bg-gray-500 shrink" />
                </div>
                {availableFields?.map(it => (
                    <div key={`availableFields-${it.id}`} className="DraggableField">
                        <label className="flex items-center content-center justify-between w-full p-[0.85rem] mb-0">
                            <div className="">
                                <p>{it.label}</p>
                                <p className="text-sm opacity-60">{it.field}</p>
                            </div>
                            <div className="flex justify-end">
                                <Checkbox
                                    checked={allSelectedFields.hasChecked(it.id)}
                                    onChange={() => allSelectedFields.toggleChecked(it.id)} />
                            </div>
                        </label>
                    </div>
                ))}
            </div>
            
            <div className="relative col-span-2">
                <div className="sticky mx-auto mt-20 top-20" style={{ maxWidth: '150px' }}>
                    <div className="flex flex-col items-center content-center">
                        <button
                            className="flex items-center content-center justify-between w-full btn btn-outline-primary"
                            title="Add all available fields"
                            disabled={allSelectedFields.allChecked}
                            onClick={allSelectedFields.toggleAllChecked}>
                            <span className="mr-2 text-sm">Select All</span>
                            <FaAngleDoubleRight className="text-xl" />
                        </button>
                        <button
                            className="
                                flex items-center content-center justify-between
                                w-full mt-3 btn btn-outline-primary"
                            title="Remove all selected fields"
                            disabled={selectedFields.checkedCount === 0}
                            onClick={() => {
                                allSelectedFields.setChecked([], true)
                                selectedFields.setChecked([], true)
                            }}>
                            <FaAngleDoubleLeft className="text-xl" />
                            <span className="mr-2 text-sm">Unselect All</span>
                        </button>
                    </div>
                </div>
            </div>
            
            <div className="col-span-5">
                <div className="flex items-center content-center justify-center">
                    <div className="w-full h-px mb-2 bg-gray-500 shrink" />
                    <h4 className="mx-3 mb-2 text-base grow shrink-0">
                        Selected Fields
                    </h4>
                    <div className="w-full h-px mb-2 bg-gray-500 shrink" />
                </div>
                {!selectedFields.checked.length && (
                    <div className="mt-6 text-center">
                        <MdInfoOutline className="w-auto mx-auto mb-3 text-4xl" />
                        <p>No fields assigned.</p>
                        <p>All export fields will be used for this template.</p>
                    </div>
                )}
                <SortableList
                    className=""
                    itemClassName=""
                    id="templateFields"
                    items={selectedTemplateFields}
                    setItems={setSelectedFields} />
            </div>
        
        </div>
        
    )
    
}

export default TemplateFieldsList
